<script setup lang="ts">
  import { Talk } from "~/entities/Talk";
  import { TalkRoomUser } from "~/entities/TalkRoomUser";

  interface Props {
    type: "reply" | "thread";
    refSrcTalk: Talk; // 参照元トーク
    isMe: boolean;
    talkRoomUsers: Record<string, TalkRoomUser>;
    ownerIds: string[];
    scroll: (targetTalkId: string) => void;
  }
  const props = defineProps<Props>();

  const refSrcLabel = props.type === "reply" ? "返信" : "スレッド";

  // 参照元ユーザー情報
  const refSrcUser = computed<TalkRoomUser | undefined>(() => {
    return props.refSrcTalk ? props.talkRoomUsers[props.refSrcTalk.userId] : undefined;
  });
  const refSrcUserName = computed<string | undefined>(() => {
    return refSrcUser.value ? refSrcUser.value.userName : undefined;
  });
  const refSrcUserIcon = computed<string | undefined>(() => {
    return refSrcUser.value ? refSrcUser.value.userImage : undefined;
  });
  const isOwnersReplyTalk = computed<boolean>(() => {
    return !!props.refSrcTalk?.userId && props.ownerIds.includes(props.refSrcTalk.userId);
  });

  // 参照元遷移 有効/無効判定
  const isMoveUpToReplySrcEnabled = computed<boolean>(() => !props.refSrcTalk?.isDeleted());

  const onClickReply = () => {
    if (isMoveUpToReplySrcEnabled.value) {
      props.scroll(props.refSrcTalk!.talkId);
    }
  };

  const refSrcThumbnailImageSrc = computed(() => {
    return props.refSrcTalk?.thumbnailImageUrl;
  });

  const refSrcContentDisp = computed<string>(() => {
    if (!props.refSrcTalk) {
      return `${refSrcLabel}元メッセージがありません`;
    }
    return props.refSrcTalk!.contents;
  });
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    class="content_wrapper d-flex"
    :class="[{ content_wrapper_for_me: isMe }, { 'cursor-pointer': isMoveUpToReplySrcEnabled }]"
    data-testid="talkroom-ref-src"
    @click="onClickReply"
  >
    <div class="avator" :class="{ avator_for_me: isMe }">
      <AtomsUserIcon
        :src="refSrcUserIcon"
        :is-owner="isOwnersReplyTalk"
        :width="30"
        :height="30"
        data-testid="talk-message-avator"
      ></AtomsUserIcon>
      <AtomsSvgIcon
        v-if="type === 'reply'"
        class="reply_icon mt-2 ml-1"
        img-path="assets/images/icon/talk-reply.svg"
        :width="18"
        :height="18"
      ></AtomsSvgIcon>
      <v-icon
        v-else
        class="thread_icon mt-1 ml-1"
        style="color: rgb(var(--v-theme-monochrome-02))"
        icon="$mdiMessageText"
        :size="20"
      />
    </div>
    <div class="flex-grow-1">
      <div class="content_username">
        {{ refSrcUserName ? `${refSrcUserName}` : `${refSrcLabel}元読込中...` }}
      </div>

      <!-- 削除済みメッセージ -->
      <div v-if="!isMoveUpToReplySrcEnabled" class="content_text">
        {{ `${refSrcLabel}元メッセージが削除されました` }}
      </div>

      <!-- 画像メッセージ -->
      <div v-else-if="refSrcTalk.messageType === 'image'" class="content_image mt-2">
        <v-img :src="refSrcThumbnailImageSrc" height="128" class="isImage" alt="thumbnail" />
        <div v-if="refSrcTalk?.note" class="mt-2" v-html="sanitizeDom(refSrcTalk.note, 20)"></div>
      </div>

      <!-- PDFメッセージ -->
      <div v-else-if="refSrcTalk.messageType === 'pdf'" class="content_text mt-2">
        <AtomsPdfFileLabel :file-name="refSrcTalk.fileName" :has-preview-button="false" />
        <div v-if="refSrcTalk?.note" class="mt-2" v-html="sanitizeDom(refSrcTalk.note, 20)"></div>
      </div>

      <!-- テキストメッセージ -->
      <div v-else class="content_text" v-html="sanitizeDom(refSrcContentDisp, 20)"></div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  .avator {
    padding: 0px 8px 0 0;
  }
  .content_wrapper {
    background-color: rgb(var(--v-theme-monochrome-06));
    padding: 8px;
  }
  .avator_for_me {
    padding: 0px 8px;
  }
  .content_wrapper_for_me {
    padding-left: 0px;
    padding-right: 10px;
  }
  .content_username {
    font-size: 10px;
    font-weight: 700;
    color: rgb(var(--v-theme-monochrome-02));
  }
  .content_image,
  .content_text {
    color: rgb(var(--v-theme-monochrome-02)) !important;
    font-size: 14px;
    word-break: break-all;
    white-space: break-spaces;
    color: rgb(var(--v-theme-monochrome-03));
    background-color: rgb(var(--v-theme-monochrome-06));
    border-radius: 10px;
    max-width: 560px;
  }
  .isImage {
    background: transparent !important;
    max-height: 300px;
    max-width: calc(100vw - 150px);
  }
</style>
